import {
  CUSTOMER_NOTICE_KINDS,
  CUSTOMER_NOTICE_LABELS,
  CUSTOMER_NOTICE_TEXT_MAX,
  resolveCustomerNoticesPolicy,
  type CustomerNoticesPolicy,
} from '@lume/shared';
import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useMemo, useRef, useState } from 'react';

import { useCapability } from '../../../hooks/use-capability';
import { conductAxisMeta } from '../../conduct/conduct-labels';
import { CONDUCT_AXES, CONDUCT_AXIS_COLUMN } from '../../conduct/conduct-types';
import { ConductAxisField } from '../../conduct/ConductAxisField';
import {
  CONDUCT_PROFILE_CAPABILITY,
  CONDUCT_PROFILE_QUERY_KEY,
  axisResolution,
  useEffectiveConductProfile,
} from '../../conduct/use-conduct-profile';
import { useSectionSave } from '../hub/save-bar-context';

import { useTenantConfig, useUpdateTenantConfig } from './use-tenant-config';

import type { SerializedTenantConfig, UpdateTenantConfigRequest } from './types';
import type { ConductAxis } from '../../conduct/conduct-types';

/**
 * Aba «Conduta da IA» da Config do Tenant — Fase 2 «Arquitetura de Conduta e Contexto».
 *
 * ── Por que AQUI, e não em `admin/ai-config` ──────────────────────────────────────────
 * Alocação é capability-driven, não gosto (`feedback_alocacao_telas_capability_driven_ui`).
 * As 5 colunas de conduta do nível Tenant vivem em `Tenant`, são servidas por
 * `GET|PATCH /v1/tenant-config` e gateadas por `tenant-config-read`/`tenant-config-manage` —
 * que é exatamente esta tela. `admin/ai-config` é OUTRA superfície: fala com
 * `/v1/tenant-ai-config/*` (modelo por agente, failover, escala de dinamismo), gateada por
 * `custom-model-selection`. Pôr conduta lá exigiria um segundo caminho para o mesmo dado —
 * e «2 caminhos para a mesma ideia» é a classe de erro registrada em
 * `feedback_quantos_caminhos_chegam_aqui`.
 *
 * ── Este é o nível que 100% dos tenants pequenos vão usar ─────────────────────────────
 * O dono de uma clínica que quer «a IA não inventando prazo e passando pra mim na dúvida»
 * resolve isso AQUI, em 5 seletores, sem nunca abrir playbook ou persona. A cascata de 3
 * níveis não aparece para ele: os outros dois níveis só existem nas telas deles, e a única
 * pista da cascata é a frase de origem em cada eixo.
 *
 * Segue o molde canônico das abas irmãs (`BehavioralTab`/`SchedulingTab`): `useState` +
 * `serverForm` derivado + diff por JSON + Salvar/Descartar explícitos. Sem If-Match — o
 * model `Tenant` não tem coluna `version` (documentado em `tenant-config.controller.ts:22-28`;
 * adicionar concorrência otimista a `Tenant` exige ADR dedicada).
 *
 * Admin Hub T8 — os botões locais Descartar/Salvar saíram; a seção registra na
 * `StickySaveBar` do domínio via `useSectionSave` (mesmo movimento do `GeneralTab`,
 * T7). Fix round 1: o `<header>` próprio (título + as duas explicações do topo,
 * incluindo a semântica de «herdar») SAIU — mesmo movimento que a T7 já tinha
 * feito em `GeneralTab`. A primeira versão desta task tinha mantido o header
 * porque o `item.efeito` do catálogo, na hora, descrevia uma feature ERRADA
 * (achado do fix round 1: `acompanhamento-silencioso`, um item irmão, tinha
 * herdado a description de uma capability de plataforma diferente — vide
 * `catalog.ts`); com o catálogo corrigido, os DOIS títulos empilhados
 * deixaram de ter justificativa aqui também. A semântica de «herdar»
 * continua visível por eixo — cada `ConductAxisField` já mostra a opção
 * «Herdar — <valor>» no seletor e a frase de origem («Definido aqui» /
 * herdado de X) abaixo dele; não há conteúdo exclusivo do header que se
 * perdeu. Ver task-8-report.md (fix round 1) para o racional completo.
 */

interface ConductTabProps {
  readonly canManage: boolean;
}

type FormState = Record<ConductAxis, string | null> & {
  /** Item 488 — não é eixo em cascata: boolean do tenant, lido pelo conductor. */
  aiSelfPresentsAsVirtual: boolean;
  /** Item 480 — avisos ao cliente, JÁ resolvidos (defaults preenchidos) — o que a tela mostra é o que o runtime faz. */
  customerNotices: CustomerNoticesPolicy;
};

export const AI_SELF_PRESENTATION_LABEL = 'Apresentação da IA';
export const CUSTOMER_NOTICES_LABEL = 'Avisos ao cliente';

function toForm(c: SerializedTenantConfig): FormState {
  return {
    aiSelfPresentsAsVirtual: c.aiSelfPresentsAsVirtual ?? false,
    customerNotices: resolveCustomerNoticesPolicy(c.customerNoticesPolicy),
    registerPolicy: c.conductRegisterPolicy ?? null,
    guidanceStyle: c.conductGuidanceStyle ?? null,
    responseLength: c.conductResponseLength ?? null,
    confirmationStyle: c.conductConfirmationStyle ?? null,
    escalationPosture: c.conductEscalationPosture ?? null,
  };
}

export function ConductTab({ canManage }: ConductTabProps): JSX.Element {
  const canReadConduct = useCapability(CONDUCT_PROFILE_CAPABILITY);
  const queryClient = useQueryClient();
  const query = useTenantConfig();
  const update = useUpdateTenantConfig();
  // Sem playbook: a cascata deste nível é Tenant ▸ plataforma. É a pergunta certa aqui.
  const effective = useEffectiveConductProfile();

  const [form, setForm] = useState<FormState | null>(null);

  useEffect(() => {
    if (query.data !== undefined) setForm(toForm(query.data));
  }, [query.data]);

  const serverForm = useMemo(
    () => (query.data !== undefined ? toForm(query.data) : null),
    [query.data],
  );

  const isDirty = useMemo(() => {
    if (form === null || serverForm === null) return false;
    return JSON.stringify(form) !== JSON.stringify(serverForm);
  }, [form, serverForm]);

  const fieldsDisabled = !canManage || update.isPending;

  // Envia SEMPRE as 5 colunas, incluindo as `null`: `null` é o valor semântico «não
  // opinei» e é o que LIMPA a coluna. Sem enviar o null, «voltar a herdar» não chegaria
  // ao banco (o campo ausente não altera nada). Usado nos dois sentidos: `buildBody(form)`
  // (o que enviar) e `buildBody(serverForm)` (os valores antigos, para o `undo`).
  const buildBody = (f: FormState): UpdateTenantConfigRequest => {
    const body: UpdateTenantConfigRequest = {};
    for (const axis of CONDUCT_AXES) {
      (body as Record<string, unknown>)[CONDUCT_AXIS_COLUMN[axis]] = f[axis];
    }
    (body as Record<string, unknown>).aiSelfPresentsAsVirtual = f.aiSelfPresentsAsVirtual;
    // Item 480 — a policy inteira (resolvida) viaja no PATCH; o Zod do engine valida cada tipo.
    (body as Record<string, unknown>).customerNoticesPolicy = f.customerNotices;
    return body;
  };

  // Labels dos eixos que MUDARAM (não os 5 — só os que o diff realmente altera),
  // para a StickySaveBar nomear o que foi salvo. `conductAxisMeta` é o mesmo
  // vocabulário que `ConductAxisField` já usa para o título de cada eixo.
  const changedLabels = useMemo(() => {
    if (form === null || serverForm === null) return [];
    const labels = CONDUCT_AXES.filter((axis) => form[axis] !== serverForm[axis]).map(
      (axis) => conductAxisMeta(axis).label,
    );
    if (form.aiSelfPresentsAsVirtual !== serverForm.aiSelfPresentsAsVirtual) {
      labels.push(AI_SELF_PRESENTATION_LABEL);
    }
    if (JSON.stringify(form.customerNotices) !== JSON.stringify(serverForm.customerNotices)) {
      labels.push(CUSTOMER_NOTICES_LABEL);
    }
    return labels;
  }, [form, serverForm]);

  const previousValues = useRef<UpdateTenantConfigRequest | null>(null);

  const handleDiscard = (): void => {
    if (serverForm !== null) setForm(serverForm);
    update.reset();
  };

  // Precisa ser chamado incondicionalmente em todo render (regra dos hooks) —
  // antes dos returns condicionais de loading abaixo.
  useSectionSave(
    isDirty && canManage && form !== null && serverForm !== null
      ? {
          sectionId: 'conduta',
          changedLabels,
          save: async () => {
            // Captura os valores ANTIGOS (server) ANTES de mutar — mesma
            // mutation reaplica isto no `undo`.
            previousValues.current = buildBody(serverForm);
            await update.mutateAsync(buildBody(form));
            // ★ Invalidar a RESOLUÇÃO, não só a config. Achado na validação viva: salvar
            // atualizava a coluna e o «Hoje: …» continuava mostrando o valor ANTIGO, porque a
            // mutação de tenant-config só reescreve o cache dela própria. Uma tela de conduta
            // que mostra efetivo velho depois de salvar é exatamente a desonestidade que esta
            // frente existe para matar — então a invalidação é parte do contrato, não polimento.
            await queryClient.invalidateQueries({ queryKey: [CONDUCT_PROFILE_QUERY_KEY] });
          },
          discard: handleDiscard,
          undo: async () => {
            if (previousValues.current !== null) {
              await update.mutateAsync(previousValues.current);
              await queryClient.invalidateQueries({ queryKey: [CONDUCT_PROFILE_QUERY_KEY] });
            }
          },
        }
      : null,
  );

  if (query.isLoading || form === null) {
    return <p className="text-sm text-text-secondary">Carregando…</p>;
  }

  return (
    <div className="max-w-2xl">
      {/* Header próprio saiu (fix round 1, T8) — DomainSection já supre título
          (item.label) + descrição (item.efeito) quando esta aba é composta no
          hub. A semântica de «herdar» segue visível por eixo (ver docblock). */}
      {!canReadConduct ? (
        <p className="rounded-lg border border-border-subtle bg-app-bg px-4 py-3 text-sm text-text-secondary">
          Seu perfil não vê a conduta resolvida. Os seletores abaixo continuam funcionando, mas sem
          mostrar de onde vem cada valor.
        </p>
      ) : null}

      {/* Item 488 — a config que manda em «como a IA se apresenta» agora tem tela. Antes só se
          mudava pela base, e a nota da persona («Limites de tom») avisava que não tinha para onde
          apontar. Não é eixo em cascata: é do tenant, ponto. */}
      <section
        className="mb-6 flex items-start justify-between gap-4 rounded-lg border border-border-subtle p-4"
        aria-label={AI_SELF_PRESENTATION_LABEL}
      >
        <div>
          <h3 className="text-sm font-medium text-text-primary">
            A IA se apresenta como atendente virtual
          </h3>
          <p className="mt-0.5 text-xs text-text-secondary">
            Ligado: ao acolher, a IA diz que é a atendente virtual da empresa. Desligado:
            apresenta-se como o atendimento e só diz que é virtual se o cliente perguntar. Vale para
            todas as conversas conduzidas pela IA e prevalece sobre os «Limites de tom» da persona.
          </p>
        </div>
        <button
          type="button"
          role="switch"
          aria-checked={form.aiSelfPresentsAsVirtual}
          aria-label="A IA se apresenta como atendente virtual"
          disabled={fieldsDisabled}
          onClick={() => {
            setForm((f) =>
              f === null ? f : { ...f, aiSelfPresentsAsVirtual: !f.aiSelfPresentsAsVirtual },
            );
          }}
          className={`relative h-5 w-9 flex-shrink-0 rounded-full transition-colors disabled:opacity-50 ${
            form.aiSelfPresentsAsVirtual ? 'bg-accent' : 'bg-border-subtle'
          }`}
        >
          <span
            className={`absolute top-0.5 h-4 w-4 rounded-full bg-white transition-transform ${
              form.aiSelfPresentsAsVirtual ? 'translate-x-[18px]' : 'translate-x-0.5'
            }`}
          />
        </button>
      </section>

      {/* Item 480 — o que o CLIENTE ouve quando muda o interlocutor. Antes nada saía: o cliente
          ficava olhando a tela sem saber se alguém vinha, e o encerramento simplesmente parava.
          Textos neutros de vertical por padrão; `{nome}` vira o nome do atendente. */}
      <section
        className="mb-6 rounded-lg border border-border-subtle p-4"
        aria-label={CUSTOMER_NOTICES_LABEL}
      >
        <h3 className="text-sm font-medium text-text-primary">Avisos ao cliente</h3>
        <p className="mt-0.5 mb-3 text-xs text-text-secondary">
          Falas curtas que o sistema envia ao cliente quando uma pessoa assume, retoma da IA ou
          encerra a conversa. Use <span className="mono">{'{nome}'}</span> para o nome do atendente.
          No WhatsApp, fora da janela de 24h a entrega depende de template aprovado.
        </p>
        <div className="space-y-3">
          {CUSTOMER_NOTICE_KINDS.map((kind) => {
            const setting = form.customerNotices[kind];
            const meta = CUSTOMER_NOTICE_LABELS[kind];
            const setSetting = (patch: Partial<{ enabled: boolean; text: string }>): void => {
              setForm((f) =>
                f === null
                  ? f
                  : {
                      ...f,
                      customerNotices: {
                        ...f.customerNotices,
                        [kind]: { ...f.customerNotices[kind], ...patch },
                      },
                    },
              );
            };
            return (
              <div key={kind} className="rounded-md border border-border-subtle p-3">
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <h4 className="text-sm text-text-primary">{meta.title}</h4>
                    <p className="mt-0.5 text-xs text-text-secondary">{meta.hint}</p>
                  </div>
                  <button
                    type="button"
                    role="switch"
                    aria-checked={setting.enabled}
                    aria-label={meta.title}
                    disabled={fieldsDisabled}
                    onClick={() => setSetting({ enabled: !setting.enabled })}
                    className={`relative h-5 w-9 flex-shrink-0 rounded-full transition-colors disabled:opacity-50 ${
                      setting.enabled ? 'bg-accent' : 'bg-border-subtle'
                    }`}
                  >
                    <span
                      className={`absolute top-0.5 h-4 w-4 rounded-full bg-white transition-transform ${
                        setting.enabled ? 'translate-x-[18px]' : 'translate-x-0.5'
                      }`}
                    />
                  </button>
                </div>
                <input
                  type="text"
                  aria-label={`Texto: ${meta.title}`}
                  maxLength={CUSTOMER_NOTICE_TEXT_MAX}
                  disabled={fieldsDisabled || !setting.enabled}
                  value={setting.text}
                  onChange={(e) => setSetting({ text: e.target.value })}
                  className="mt-2 w-full rounded border border-border-subtle bg-surface px-3 py-2 text-sm text-text-primary focus:border-accent focus:outline-none disabled:opacity-60"
                />
              </div>
            );
          })}
        </div>
      </section>

      <div className="space-y-4">
        {CONDUCT_AXES.map((axis) => (
          <ConductAxisField
            key={axis}
            axis={axis}
            level="tenant"
            resolution={axisResolution(effective.data, axis)}
            levels={effective.data?.levels}
            value={form[axis]}
            disabled={fieldsDisabled}
            onChange={(next) => {
              setForm((f) => (f === null ? f : { ...f, [axis]: next }));
            }}
          />
        ))}
      </div>

      {/* Descartar/Salvar saíram — vivem na StickySaveBar do domínio (Admin Hub
          T8); sucesso/erro do save viram toast da barra, não mensagem local. */}
      {!canManage && (
        <p className="mt-6 rounded-lg border border-border-subtle bg-app-bg px-4 py-3 text-sm text-text-secondary">
          Você tem acesso de leitura. A edição da conduta é restrita ao administrador do tenant.
        </p>
      )}
    </div>
  );
}
